import { getCurrentInstance } from 'vue';
import { useRouter } from 'vue-router/composables';
import V2CatalogBulkPriceDialog from '@/components/v2/catalog/dialogs/V2CatalogBulkPriceDialog.vue';
import V2CatalogCategoryDeleteDialog from '@/components/v2/catalog/dialogs/V2CatalogCategoryDeleteDialog.vue';
import V2CatalogMoveDialog from '@/components/v2/catalog/dialogs/V2CatalogMoveDialog.vue';
import { useConfirmDialog } from '@/composition/use-confirm-dialog';
import { useModal } from '@/composition/use-modal';
import type {
  TCategoryMenuKey,
  TMoreMenuKey,
  TRowMenuKey,
} from '@/domain/catalog/catalog-menus';
import { scheduleBadgeText } from '@/domain/catalog/catalog-status-text';
import { moveTargets } from '@/domain/catalog/move-targets';
import { scheduleSummary } from '@/domain/catalog/schedule-form';
import { IBulkPriceChange, TCategoryDeleteItems } from '@/interfaces/ICatalogV2';
import { ICategory } from '@/interfaces/ICategory';
import { IItem } from '@/interfaces/IItem';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { getTopLevelDomain } from '@/utils/domain.utils';
import { ignoreNavigationFailure } from '@/utils/navigation.utils';
import type { ICatalogPageState } from './catalog-page-state';
import type { ICatalogActions } from './use-catalog-actions';
import type { ICatalogData } from './use-catalog-data';
import type { ICatalogUrl } from './use-catalog-url';
import type { IBulkOutcome } from './use-catalog-writes';

export interface ICatalogCommandsDeps {
  state: ICatalogPageState;
  data: ICatalogData;
  url: ICatalogUrl;
  actions: ICatalogActions;
  jumpToCategory: (categoryId: number) => Promise<void>;
  /** Docked editor (desktop): opening another target switches it in place. */
  editorDocked: () => boolean;
  /** «¿Descartar el nuevo orden?»; true to leave reorder mode. */
  confirmReorderDiscard: () => Promise<boolean>;
  /** «¿Descartar los cambios…?» of the open editor; true to close it. */
  confirmEditorDiscard: () => Promise<boolean>;
}

/** Distinct modal names: a dialog opened twice must not hide the other. */
let dialogSequence = 0;

function dialogOptions() {
  return {
    adaptive: true,
    width: 480,
    maxWidth: window.innerWidth * 0.95,
    height: 'auto',
    clickToClose: true,
  };
}

/**
 * What the entries of the Menú page's ⋯ menus do (plan §5.7, §5.8,
 * §5.15): row, category and «Más acciones» commands, plus the dialogs they
 * open (move products, delete a category) and the reorder / selection
 * modes. Call it in the page's `setup` (dialogs need the page instance).
 */
export function useCatalogCommands(deps: ICatalogCommandsDeps) {
  const { state, data, url, actions } = deps;
  const router = useRouter();
  const modals = useModal();
  const confirmDialog = useConfirmDialog();
  const restaurantStore = useRestaurantStore();
  const proxy = getCurrentInstance()!.proxy;
  const t = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const tc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  function branchName(): string {
    return restaurantStore.selectedBranch?.name ?? '';
  }

  /** The move dialogs name each category's schedule («lun a vie · 12:00 a 16:00»). */
  function targetsWithout(categoryId: number | null) {
    return moveTargets(
      data.sections.value,
      categoryId,
      data.branchId.value,
      (section) =>
        scheduleBadgeText(t, scheduleSummary(section.category.hourRanges))
    );
  }

  /** A page of the store (`/{stringId}{path}`). */
  function go(path: string) {
    const stringId = data.restaurant.value?.stringId;
    if (!stringId) return;
    router.push(`/${stringId}${path}`).catch(ignoreNavigationFailure);
  }

  //#region Modes

  /**
   * Reorder and selection take the page (their bars sit where the docked
   * editor's «Guardar» is): the editor closes first, asking if unsaved.
   */
  async function closeEditorForMode(): Promise<boolean> {
    if (!state.editor) return true;
    if (!(await deps.confirmEditorDiscard())) return false;
    url.closeEditor();
    return true;
  }

  async function startReorder(categoryId: number | null) {
    if (!(await closeEditorForMode())) return;
    state.panel = null;
    state.selectedIds = {};
    state.reorderCategoryId = categoryId;
    state.mode = 'reorder';
  }

  /** Back to the list (after «Guardar orden», or when nothing is pending). */
  function exitReorder() {
    state.reorderDirty = false;
    state.reorderCategoryId = null;
    state.mode = 'browse';
  }

  /** ✕ / «Cancelar»: asks first when the new order is not saved. */
  async function closeReorder() {
    if (state.mode !== 'reorder') return;
    if (await deps.confirmReorderDiscard()) exitReorder();
  }

  async function startSelecting(categoryId: number | null) {
    if (!(await closeEditorForMode())) return;
    state.panel = null;
    state.mode = 'select';
    if (categoryId !== null) void deps.jumpToCategory(categoryId);
  }

  //#endregion

  //#region Dialogs

  /** «Mover a otra categoría»: the chosen category, or null. */
  async function chooseMoveTarget(
    title: string,
    fromCategoryId: number | null
  ): Promise<ICategory | null> {
    dialogSequence += 1;
    const dialogName = `catalog-move-${dialogSequence}`;
    const targetId = await modals.showDynamicDialog<number | null>(
      V2CatalogMoveDialog,
      {
        dialogName,
        title,
        targets: targetsWithout(fromCategoryId),
        branchName: branchName(),
      },
      dialogName,
      dialogOptions()
    );
    if (typeof targetId !== 'number') return null;
    return data.categoryById.value.get(targetId) ?? null;
  }

  /** Moves products (a row or a selection) to a category the owner picks. */
  async function moveProducts(items: readonly IItem[]): Promise<IBulkOutcome> {
    if (!items.length) return { appliedIds: [] };
    const from = items.every((item) => item.categoryId === items[0].categoryId)
      ? items[0].categoryId
      : null;
    const title =
      items.length === 1
        ? t('menuPage.move.title')
        : tc('menuPage.move.titleMany', items.length, { n: items.length });
    const target = await chooseMoveTarget(title, from);
    if (!target) return { appliedIds: [] };
    // Products already there stay where they are (they count as done).
    const already = items
      .filter((item) => item.categoryId === target.id)
      .map((item) => item.id);
    const moving = items.filter((item) => item.categoryId !== target.id);
    if (!moving.length) return { appliedIds: already };
    const outcome = await actions.moveItems(moving, target);
    return outcome.appliedIds.length
      ? { appliedIds: [...outcome.appliedIds, ...already] }
      : outcome;
  }

  /** «Cambiar precio» of a selection: the dialog, then one bulk write. */
  async function changeBulkPrices(
    items: readonly IItem[]
  ): Promise<IBulkOutcome> {
    if (!items.length) return { appliedIds: [] };
    dialogSequence += 1;
    const dialogName = `catalog-bulk-price-${dialogSequence}`;
    const changes = await modals.showDynamicDialog<IBulkPriceChange[] | null>(
      V2CatalogBulkPriceDialog,
      {
        dialogName,
        items: [...items],
        currency: data.rowContext.value.currency,
        country: data.rowContext.value.country,
      },
      dialogName,
      { ...dialogOptions(), width: 520, scrollable: true, clickToClose: false }
    );
    if (!changes || !changes.length) return { appliedIds: [] };
    return actions.saveBulkPrices(changes);
  }

  /** «Eliminar categoría»: a plain confirm when empty, else move or delete. */
  async function deleteCategory(category: ICategory) {
    const section = data.sections.value.find(
      (entry) => entry.category.id === category.id
    );
    const itemCount = section?.items.length ?? 0;
    if (!itemCount) {
      const confirmed = await confirmDialog.confirm({
        title: t('menuPage.categoryDelete.title', { name: category.name }),
        message: t('menuPage.categoryDelete.emptyMessage'),
        acceptText: t('menuPage.categoryDelete.accept'),
        tone: 'danger',
      });
      if (confirmed)
        await actions.deleteCategory(category, { items: 'delete' });
      return;
    }

    dialogSequence += 1;
    const dialogName = `catalog-category-delete-${dialogSequence}`;
    const choice = await modals.showDynamicDialog<TCategoryDeleteItems | null>(
      V2CatalogCategoryDeleteDialog,
      {
        dialogName,
        name: category.name,
        itemCount,
        targets: targetsWithout(category.id),
        branchName: branchName(),
      },
      dialogName,
      { ...dialogOptions(), width: 520, clickToClose: false }
    );
    if (choice) await actions.deleteCategory(category, choice);
  }

  //#endregion

  //#region Menu commands

  /** A product row's ⋯ entry; `anchor` is the ⋯ button. */
  function runRowCommand(
    key: TRowMenuKey,
    item: IItem,
    anchor: HTMLElement | null
  ) {
    state.panel = null;
    switch (key) {
      case 'edit':
        url.openItem(item.id, deps.editorDocked());
        return;
      case 'price':
        state.panel = { kind: 'price', targetId: item.id, anchor };
        return;
      case 'duplicate':
        void actions.duplicate(item);
        return;
      case 'move':
        void moveProducts([item]);
        return;
      case 'view':
        actions.viewInMenu(item);
        return;
      case 'delete':
        void actions.deleteItems([item]);
        return;
    }
  }

  function runCategoryCommand(key: TCategoryMenuKey, category: ICategory) {
    state.panel = null;
    switch (key) {
      case 'edit':
        url.openCategory(category.id, deps.editorDocked());
        return;
      case 'addProduct':
        url.openNewItem(category.id);
        return;
      case 'hideProducts':
        void actions.setCategoryProductsVisible(category, false);
        return;
      case 'showProducts':
        void actions.setCategoryProductsVisible(category, true);
        return;
      case 'reorder':
        void startReorder(category.id);
        return;
      case 'select':
        void startSelecting(category.id);
        return;
      case 'delete':
        void deleteCategory(category);
        return;
    }
  }

  function runMoreCommand(key: TMoreMenuKey) {
    state.panel = null;
    switch (key) {
      case 'newCategory':
        url.openCategory('new', deps.editorDocked());
        return;
      case 'reorder':
        void startReorder(null);
        return;
      case 'select':
        void startSelecting(null);
        return;
      case 'options':
        go('/menu/options');
        return;
      case 'import':
        go('/menu/import');
        return;
      case 'bulkPrices':
        go('/catalog/prices');
        return;
      case 'stickers':
        go('/stickers');
        return;
      case 'settings':
        go('/catalog/settings');
        return;
      case 'viewMenu': {
        const stringId = data.restaurant.value?.stringId;
        if (stringId)
          window.open(
            `https://${getTopLevelDomain()}/${stringId}`,
            '_blank',
            'noopener'
          );
        return;
      }
    }
  }

  //#endregion

  return {
    startReorder,
    exitReorder,
    closeReorder,
    startSelecting,
    moveProducts,
    changeBulkPrices,
    deleteCategory,
    runRowCommand,
    runCategoryCommand,
    runMoreCommand,
  };
}

export type ICatalogCommands = ReturnType<typeof useCatalogCommands>;
